import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'
import { connect } from 'react-redux'

import {
    Currency,
    Dialog,
    InputNumber,
    MemberName,
    Select,
} from 'react-wot-bootstrap'

import * as dialogActions from 'state/actions/CMT/Dialog'
import { treasuryPropTypes } from 'propTypes'
import { MONEY_TYPES } from 'constants'
import { CURRENCIES } from 'helpers/i18n'

import { DIALOG_BUTTON_CANCEL_CAPTION } from '../translations'

import {
    DIALOG_ALLOCATING_RESOURCES_AFTER_DIVIDE_TITLE,
    DIALOG_ALLOCATING_RESOURCES_AMOUNT_OF_CHARGES_TITLE,
    DIALOG_ALLOCATING_RESOURCES_BEFORE_DIVIDE_TITLE,
    DIALOG_ALLOCATING_RESOURCES_DIVIDE_EACH_TITLE,
    DIALOG_ALLOCATING_RESOURCES_DIVIDE_TO_MEMBER_TITLE,
    DIALOG_ALLOCATING_RESOURCES_DIVIDE_TO_MEMBERS_TITLE,
    DIALOG_ALLOCATING_RESOURCES_DIVIDE_TO_ONE_TITLE,
    DIALOG_ALLOCATING_RESOURCES_SELECT_CURRENCY,
    DIALOG_ALLOCATING_RESOURCES_SUBMIT_BUTTON_CAPTION,
    DIALOG_ALLOCATING_RESOURCES_TITLE,
} from './translations'

import { DialogAllocatingResourcesPlayersTooltip } from './tooltips'

import styles from '../Dialogs.scss'

const propTypes = {
    selectedMembers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    treasury: treasuryPropTypes.isRequired,
    isOpened: PropTypes.bool,

    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
}

export class AllocatingResourcesDialog extends React.Component {
    constructor(props) {
        super(props)
        let currency

        if (MONEY_TYPES.gold in props.treasury) currency = MONEY_TYPES.gold
        else if (MONEY_TYPES.credits in props.treasury) currency = MONEY_TYPES.credits
        else if (MONEY_TYPES.crystal in props.treasury) currency = MONEY_TYPES.crystal

        this.state = {
            toEach: 0,
            currency,
        }
    }

    onChangeAmountToEach = toEach => this.setState({ toEach })

    onSubmit = () => this.props.onSubmit(this.state.toEach, this.state.currency)

    onCurrencyChange = newCurrency => this.setState({ currency: newCurrency, toEach: 0 })

    render() {
        let members = null
        let tooltip = null

        const currency = this.state.currency
        const totalAmount = this.props.treasury[currency]

        const selectedMembersCount = this.props.selectedMembers.length
        const isNeedTooltip = selectedMembersCount > 10
        if (selectedMembersCount === 1) {
            members = <MemberName name={this.props.selectedMembers[0].name} />
        } else if (selectedMembersCount > 1) {
            members = this.props.selectedMembers
                .map(member => <MemberName key={member.id} name={member.name} />)
            tooltip = isNeedTooltip ?
                <DialogAllocatingResourcesPlayersTooltip children={members} /> :
                null

            members = isNeedTooltip ?
                <span className={styles.membersCount}>{selectedMembersCount}</span> :
                <div className={styles.members}>{members}</div>
        }

        const maxAmountToEach = selectedMembersCount ?
            Math.floor(totalAmount / selectedMembersCount) :
            0

        const dropDownItems = [MONEY_TYPES.gold, MONEY_TYPES.credits, MONEY_TYPES.crystal]
            .filter(currency => currency in this.props.treasury)
            .map(currency => ({ caption: CURRENCIES[currency], value: currency }))

        const amountToCharge = this.state.toEach * selectedMembersCount
        const treasuryCaptionClassName = classNames(styles.caption, styles['caption__treasury'])
        return (
            <Dialog
                title={DIALOG_ALLOCATING_RESOURCES_TITLE}
                submitCaption={DIALOG_ALLOCATING_RESOURCES_SUBMIT_BUTTON_CAPTION}
                closeCaption={DIALOG_BUTTON_CANCEL_CAPTION}
                isOpened={this.props.isOpened}
                isDisabledSubmit={amountToCharge === 0}
                onSubmit={this.onSubmit}
                onClose={this.props.onClose}
            >
                <div className={styles.base}>
                    <div
                        className={styles.header}
                        data-tip={isNeedTooltip}
                        data-for={isNeedTooltip ? DialogAllocatingResourcesPlayersTooltip.id : null}
                    >
                        {selectedMembersCount > 1 ?
                            DIALOG_ALLOCATING_RESOURCES_DIVIDE_TO_MEMBERS_TITLE :
                            DIALOG_ALLOCATING_RESOURCES_DIVIDE_TO_MEMBER_TITLE
                        }
                        &ensp;
                        {members}
                        {tooltip}
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.body}>
                        <div className={styles.row}>
                            <div className={treasuryCaptionClassName}>
                                {DIALOG_ALLOCATING_RESOURCES_SELECT_CURRENCY}
                            </div>
                            <div className={classNames(styles.value, styles.value__currency)}>
                                <Select
                                    items={dropDownItems}
                                    selectedItem={currency}
                                    onItemChange={this.onCurrencyChange}
                                    width={113}
                                />
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div className={treasuryCaptionClassName}>
                                {DIALOG_ALLOCATING_RESOURCES_BEFORE_DIVIDE_TITLE}
                            </div>
                            <div className={styles.value}>
                                <Currency type={currency} value={totalAmount} />
                            </div>
                        </div>
                    </div>
                    <div className={styles.dividerArrow} />
                    <div className={styles.body}>
                        <div className={styles.row}>
                            <div className={treasuryCaptionClassName}>
                                {selectedMembersCount > 1 ?
                                    DIALOG_ALLOCATING_RESOURCES_DIVIDE_EACH_TITLE :
                                    DIALOG_ALLOCATING_RESOURCES_DIVIDE_TO_ONE_TITLE
                                }
                            </div>
                            <div className={styles.value}>
                                <Currency type={currency} />
                                <div className={styles.input}>
                                    <InputNumber
                                        value={this.state.toEach}
                                        valueMax={maxAmountToEach}
                                        type={currency === MONEY_TYPES.gold ? 'gold' : 'regular'}
                                        isFocusOn
                                        onChange={this.onChangeAmountToEach}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div className={treasuryCaptionClassName}>
                                {DIALOG_ALLOCATING_RESOURCES_AMOUNT_OF_CHARGES_TITLE}
                            </div>
                            <div className={styles.value}>
                                <Currency type={currency} value={amountToCharge} />
                            </div>
                        </div>
                    </div>
                    <div className={styles.dividerArrow} />
                    <div className={styles.body}>
                        <div className={styles.row}>
                            <div className={treasuryCaptionClassName}>
                                {DIALOG_ALLOCATING_RESOURCES_AFTER_DIVIDE_TITLE}
                            </div>
                            <div className={styles.value}>
                                <Currency type={currency} value={totalAmount - amountToCharge} />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        )
    }
}

AllocatingResourcesDialog.propTypes = propTypes

const mapStateToProps = (state, ownProps) => {
    const members = state.members.members
    const selectedMembers =
        ownProps.selectedMembers ||
        members.filter((member) => state.membersOperation.selectedIds.includes(member.id))
    return {
        selectedMembers,
        treasury: state.clan.treasury,
    }
}

const mapDispatchToProps = {
    onClose: dialogActions.hideDialog,
}

export default connect(mapStateToProps, mapDispatchToProps)(AllocatingResourcesDialog)



// WEBPACK FOOTER //
// ./containers/ClanManagement/components/Dialogs/AllocatingResources/AllocatingResources.js